import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';

import {
	createProductSpecificAnalyticsIntegrationPayload,
	ErrorType,
	Severity,
	Product,
	Team,
} from '@atlassian/growth-commons-error-analytics';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';

import type {
	useIsEligibleForAiInEditorQuery$data,
	useIsEligibleForAiInEditorQuery$variables,
} from './__generated__/useIsEligibleForAiInEditorQuery.graphql';

export const useIsEligibleForAiInEditor = () => {
	const { edition, isLoggedIn, cloudId } = useSessionData();

	const skip =
		!isLoggedIn ||
		!cloudId ||
		edition !== ConfluenceEdition.FREE ||
		!fg('twcg-530_confluence_ai_in_editor_eot_gate');

	const { data, error, loading } = useQueryInterop<
		useIsEligibleForAiInEditorQuery$data,
		useIsEligibleForAiInEditorQuery$variables
	>(
		graphql`
			query useIsEligibleForAiInEditorQuery($cloudId: ID!) {
				license {
					billingSourceSystem
				}
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: "confluence.ondemand") {
						entitlement {
							transactionAccount {
								isBillToPresent
							}
							experienceCapabilities {
								changeOfferingV2(offeringName: "Premium") {
									isAvailableToUser
									experienceUrl
								}
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				cloudId,
			},
			skip,
			fetchPolicy: 'cache-first',
			client: getAGGClient(),
			onError: (error: Error) => {
				createProductSpecificAnalyticsIntegrationPayload({
					product: Product.CONFLUENCE,
					team: Team.GROWTH_LUNA,
					experimentMeta: {
						experimentKey: 'twcg-530_confluence_ai_in_editor_eot',
					},
					errorType: ErrorType.GRAPHQL,
					errorMessage: error?.message,
					severity: Severity.MEDIUM,
					source: 'useIsEligibleForAiInEditor',
					additionalAttributes: {
						cloudId,
					},
				});
			},
		},
	);

	const isFreeEdition = edition === ConfluenceEdition.FREE;

	const tenantContext = data?.tenantContexts?.[0];
	const entitlementInfo = tenantContext?.entitlementInfo;
	const entitlement = entitlementInfo?.entitlement;
	const experienceCapabilities = entitlement?.experienceCapabilities;
	const changeOfferingV2 = experienceCapabilities?.changeOfferingV2;
	const transactionAccount = entitlement?.transactionAccount;

	const hasCCPBillingSystem = data?.license?.billingSourceSystem === 'CCP';
	const canUpgradeToPremium = changeOfferingV2?.isAvailableToUser === true;
	const hasPaymentDetails = transactionAccount?.isBillToPresent === true;
	const experienceUrl = changeOfferingV2?.experienceUrl || null;

	const isEligible = useMemo(() => {
		return (
			isFreeEdition &&
			isLoggedIn &&
			Boolean(cloudId) &&
			hasCCPBillingSystem &&
			canUpgradeToPremium &&
			!hasPaymentDetails
		);
	}, [
		isFreeEdition,
		isLoggedIn,
		cloudId,
		hasCCPBillingSystem,
		canUpgradeToPremium,
		hasPaymentDetails,
	]);

	return {
		isEligible,
		edition,
		canUpgrade: canUpgradeToPremium,
		loading,
		error: error?.message || null,
		experienceUrl,
	};
};
